import { useState, useRef, useEffect } from "react";
import ErrorMessage from "../../components/common/ErrorMessage";
import Qtest from "../../services/Qtest";
import Jira from "../../services/Jira";
import Projects from "../../services/Projects";
import AccessibilityConformancePercentChart from "../../components/charts/defects/accessibility/AccessibilityConformancePercentChart";
import AccessibilityStatusChart from "../../components/charts/defects/accessibility/AccessibilityStatusChart";
import DefectAccessibilitySeverityChart from "../../components/charts/defects/accessibility/DefectAccessibilitySeverityChart";
import DefectCountAccessibilityChart from "../../components/charts/defects/defects/DefectCountAccessibilityChart";
import AccessibilityLevelsChart from "../../components/charts/defects/accessibility/AccessibilityLevelsChart";
import useProjectDetails from "../../components/hooks/useProjectDetails";
import { saveAs } from "file-saver";

export default function AccessibilityReport({
  token,
  changeStyle,
  currentProjectId,
  setCurrentProjectId,
}) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const reportTableContainer = useRef(null);

  const [
    qtestAccessibilityExecutionTypeResponse,
    setQtestAccessibilityExecutionTypeResponse,
  ] = useState(null);
  const [testCasesDetails, setTestCasesDetails] = useState(null);

  const [relatedIssueMap, setRelatedIssueMap] = useState(null);

  const [relatedCurrentMap, setRelatedCurrentMap] = useState(null);

  const [severityIssueMap, setSeverityIssueMap] = useState(null);

  const [
    issuesByAccessibilitySeverityMap,
    setIssuesByAccessibilitySeverityMap,
  ] = useState(null);

  const [levelsIssueMap, setLevelsIssueMap] = useState(null);

  const [defectByAccessibilityLevelsMaps, setDefectByAccessibilityLevelsMaps] =
    useState(null);

  const { projectDetails, setProjectDetails } = useProjectDetails();

  const [scopeOfEvaluation, setScopeOfEvaluation] = useState("");

  const [productDescription, setProductDescription] = useState("");

  const [evaluationMethods, setEvaluationMethods] = useState("");

  const [standardsGuidelines, setStandardsGuidelines] = useState("");

  const [testPlan, setTestPlan] = useState("");

  const [
    accessibilityConformancePercentChartUrl,
    setAccessibilityConformancePercentChartUrl,
  ] = useState(null);

  const [accessibilityStatusChartUrl, setAccessibilityStatusChartUrl] =
    useState(null);

  const [
    defectAccessibilitySeverityChartUrl,
    setDefectAccessibilitySeverityChartUrl,
  ] = useState(null);

  const [
    defectCountAccessibilityChartUrl,
    setDefectCountAccessibilityChartUrl,
  ] = useState(null);

  const [accessibilityLevelsChartUrl, setAccessibilityLevelsChartUrl] =
    useState(null);

  async function fetchAccessibilityMetrics() {
    setLoading(true);
    const response = await Qtest.getAccessibilityConformance(
      token,
      currentProjectId
    );

    if (response.message) {
      setErrorMessage(response.message);
    } else {
      setQtestAccessibilityExecutionTypeResponse(
        response.qtestAccessibilityExecutionTypeResponse
      );
      setTestCasesDetails(response.testCasesDetails);
    }

    setLoading(false);
  }

  async function getAccessibilityRelated() {
    setLoading(true);
    const data = await Jira.getAccessibilityDefectRelated(
      token,
      currentProjectId
    );
    if (data?.errMsg) {
      setErrorMessage(data.errMsg);
    } else {
      setRelatedIssueMap(data.issueMap);
      setRelatedCurrentMap(data.currentMap);
    }
    setLoading(false);
  }

  async function getAccessibilitySeverity() {
    setLoading(true);
    const data = await Jira.getAccessibilitySeverity(token, currentProjectId);
    if (data?.errMsg) {
      setErrorMessage(data.errMsg);
    } else {
      setIssuesByAccessibilitySeverityMap(
        data.data.issuesByAccessibilitySeverityMap
      );
      setSeverityIssueMap(data.data.issueMap);
    }
    setLoading(false);
  }

  async function getAccessibilityLevelsJiraInfo() {
    setLoading(true);
    const data = await Jira.getAccessibilityLevelsJiraInfo(
      token,
      currentProjectId
    );
    if (data?.errMsg) {
      setErrorMessage(data.errMsg);
    } else {
      setDefectByAccessibilityLevelsMaps(
        data.data.defectByAccessibilityLevelsMaps
      );

      setLevelsIssueMap(data.data.issueMap);
    }
    setLoading(false);
  }

  async function getProjectDetails(token, projectId) {
    try {
      const resp = await Projects.getProjectDetails(token, projectId);
      return resp;
    } catch (err) {
      return {
        message:
          "Error getting project details (" + err.message
            ? err.message
            : JSON.stringify(err) + ")",
      };
    }
  }

  useEffect(() => {
    getProjectDetails(token, currentProjectId);
    fetchAccessibilityMetrics();
    getAccessibilityRelated();
    getAccessibilitySeverity();
    getAccessibilityLevelsJiraInfo();
    getStatusReportInfo(token, currentProjectId);
  }, []);

  async function saveStatusReportInfo(token, detailsObj) {
    try {
      const response = await Projects.postStatusAccessibilityReportInfo(
        token,
        detailsObj
      );
      return response;
    } catch (err) {
      return {
        message:
          "Error getting project details (" + err.message
            ? err.message
            : JSON.stringify(err) + ")",
      };
    }
  }

  async function getStatusReportInfo(token, projectId) {
    try {
      const response = await Projects.getStatusAccessibilityReportInfo(
        token,
        projectId
      );
      setScopeOfEvaluation(response.scopeOfEvaluation);
      setProductDescription(response.productDescription);
      setEvaluationMethods(response.evaluationMethods);
      setStandardsGuidelines(response.standardsGuidelines);
      setTestPlan(response.testPlan);
    } catch (err) {
      return {
        message:
          "Error getting project details (" + err.message
            ? err.message
            : JSON.stringify(err) + ")",
      };
    }
  }

  const handleDownload = async () => {
    const response = await saveStatusReportInfo(token, {
      projectId: currentProjectId,
    });

    const css =
      "<style>" +
      //'@page WordSection1{size: 841.95pt 595.35pt;mso-page-orientation: landscape;}' +
      "div#report_main_container_1" +
      "table{border-collapse:collapse;}td{border:1px gray solid;width:5em;padding:2px;}" +
      "textarea{border:none;width:100%;height:100%;padding:0;overflow:hidden;resize:none}" +
      "-webkit-scrollbar{display:none;}" +
      "</style>";

    let html = reportTableContainer.current.innerHTML;

    let blob = new Blob(["\ufeff", css + html], {
      type: "application/msword",
    });

    saveAs(
      blob,
      "QE Status report - " +
        new Date().toLocaleDateString() +
        " " +
        new Date().toLocaleTimeString() +
        " - " +
        projectDetails?.projectName +
        ".doc"
    );
  };

  function handleSaveReportInfo() {
    const payload = {
      scopeOfEvaluation,
      productDescription,
      evaluationMethods,
      standardsGuidelines,
      testPlan,
      projectId: currentProjectId,
    };

    saveStatusReportInfo(token, payload);
  }

  return (
    <div
      id="report_main_container_1"
      className="bg-light  container-fluid pt-3 pb-3"
      style={
        loading
          ? {
              pointerEvents: "none",
              opacity: "0.4",
            }
          : {}
      }
    >
      {loading && (
        <div className="d-flex justify-content-center bg-transparent align-items-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <h4 className="ms-4">QE Status Report</h4>
      <ErrorMessage errorMessage={errorMessage} />
      <div className="ms-4 mt-4">
        <button type="button" onClick={handleDownload}>
          Download
        </button>
      </div>
      <div id="download_link"></div>
      <div className="row ms-3 mt-2 me-3">
        <div className="col-lg-9 col-xl-9" ref={reportTableContainer}>
          <table
            id="reportTable"
            className="table table-bordered table-responsive table-condensed table-sm bg-white"
            style={{ border: "1px solid grey", borderCollapse: "collapse" }}
          >
            <tbody>
              <tr
                style={{ backgroundColor: "#428bca", color: "rgb(33, 37, 41)" }}
              >
                <td colSpan="2">
                  {projectDetails?.projectName +
                    " - QE Status Report - " +
                    new Date().toLocaleDateString() +
                    " " +
                    new Date().toLocaleTimeString()}
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Scope of Evaluation:</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  <textarea
                    className="tableTextArea"
                    value={scopeOfEvaluation}
                    onChange={(e) => setScopeOfEvaluation(e.target.value)}
                    onBlur={(e) => handleSaveReportInfo()}
                  />
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Product Description</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  <textarea
                    className="tableTextArea"
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    onBlur={(e) => handleSaveReportInfo()}
                  />
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Evaluation Methods</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  <textarea
                    className="tableTextArea"
                    value={evaluationMethods}
                    onChange={(e) => setEvaluationMethods(e.target.value)}
                    onBlur={(e) => handleSaveReportInfo()}
                  />
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Standards/Guidelines</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  <textarea
                    className="tableTextArea"
                    value={standardsGuidelines}
                    onChange={(e) => setStandardsGuidelines(e.target.value)}
                    onBlur={(e) => handleSaveReportInfo()}
                  />
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Test Plan</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  <textarea
                    className="tableTextArea"
                    value={testPlan}
                    onChange={(e) => setTestPlan(e.target.value)}
                    onBlur={(e) => handleSaveReportInfo()}
                  />
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Test Execution Summary</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  {accessibilityConformancePercentChartUrl === null && (
                    <AccessibilityConformancePercentChart
                      qtestAccessibilityExecutionTypeResponse={
                        qtestAccessibilityExecutionTypeResponse
                      }
                      testCasesDetails={testCasesDetails}
                      currentIteration="Overall"
                      setModalData={() => {}}
                      setShowModal={() => {}}
                      event={true}
                      setImageUrl={setAccessibilityConformancePercentChartUrl}
                    />
                  )}
                  <div id="open-and-closed-defects-img-container">
                    {accessibilityConformancePercentChartUrl && (
                      <img
                        id="open-and-closed-defects-img"
                        className="img-fluid float-start"
                        src={accessibilityConformancePercentChartUrl}
                        alt=""
                      />
                    )}
                  </div>
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">Defect Summary</td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  {accessibilityStatusChartUrl === null && (
                    <AccessibilityStatusChart
                      issueMap={relatedIssueMap}
                      currentMap={relatedCurrentMap}
                      currentIteration="Overall"
                      setModalData={() => {}}
                      setShowModal={() => {}}
                      event={true}
                      setImageUrl={setAccessibilityStatusChartUrl}
                    />
                  )}
                  <div id="open-and-closed-defects-img-container">
                    {accessibilityStatusChartUrl && (
                      <img
                        id="open-and-closed-defects-img"
                        className="img-fluid float-start"
                        src={accessibilityStatusChartUrl}
                        alt=""
                      />
                    )}
                  </div>
                  {defectAccessibilitySeverityChartUrl === null && (
                    <DefectAccessibilitySeverityChart
                      currentIteration="Overall"
                      issueMap={severityIssueMap}
                      issuesByAccessibilitySeverityMap={
                        issuesByAccessibilitySeverityMap
                      }
                      setModalData={() => {}}
                      setShowModal={() => {}}
                      event={true}
                      setImageUrl={setDefectAccessibilitySeverityChartUrl}
                    />
                  )}
                  <div id="open-and-closed-defects-img-container">
                    {defectAccessibilitySeverityChartUrl && (
                      <img
                        id="open-and-closed-defects-img"
                        className="img-fluid float-start"
                        src={defectAccessibilitySeverityChartUrl}
                        alt=""
                      />
                    )}
                  </div>
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">
                  Defect Count by Accessibility Criteria
                </td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  {defectCountAccessibilityChartUrl === null && (
                    <DefectCountAccessibilityChart
                      currentProjectId={currentProjectId}
                      currentIteration="Overall"
                      token={token}
                      event={true}
                      setImageUrl={setDefectCountAccessibilityChartUrl}
                    />
                  )}
                  <div id="open-and-closed-defects-img-container">
                    {defectCountAccessibilityChartUrl && (
                      <img
                        id="open-and-closed-defects-img"
                        className="img-fluid float-start"
                        src={defectCountAccessibilityChartUrl}
                        alt=""
                      />
                    )}
                  </div>
                </td>
              </tr>
              <tr className="m-0 p-0">
                <td className="col-sm-2">
                  Defect Count by Accessibility Level
                </td>
                <td id="rptSummaryTD" className="m-0 p-0">
                  {accessibilityLevelsChartUrl === null && (
                    <AccessibilityLevelsChart
                      issueMap={levelsIssueMap}
                      currentIteration="Overall"
                      defectByAccessibilityLevelsMaps={
                        defectByAccessibilityLevelsMaps
                      }
                      setModalData={() => {}}
                      setShowModal={() => {}}
                      event={true}
                      setImageUrl={setAccessibilityLevelsChartUrl}
                    />
                  )}
                  <div id="open-and-closed-defects-img-container">
                    {accessibilityLevelsChartUrl && (
                      <img
                        id="open-and-closed-defects-img"
                        className="img-fluid float-start"
                        src={accessibilityLevelsChartUrl}
                        alt=""
                      />
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
